import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Page, PageScroll, EmptyState } from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import type { SiteInfo } from './CalendarMultiView'
import type { GanttDeepLink, GanttTask, ScheduleDetailTask } from '../types/gantt.types'
import type { ListDisplayItem } from '../types/scheduleList.types'
import { useSchedulerHost } from '../context/SchedulerHostContext'
import { GanttInfoBar } from '../components/gantt/GanttInfoBar'
import { GanttRemarkBar } from '../components/gantt/GanttRemarkBar'
import { SignalTodoPanel } from '../components/SignalTodoPanel'
import { ScheduleListSummaryBar } from '../components/list/ScheduleListSummaryBar'
import { ScheduleListFilterBar } from '../components/list/ScheduleListFilterBar'
import { ScheduleListTable } from '../components/list/ScheduleListTable'
import { ScheduleListLoading, ScheduleListEmpty, ScheduleListBlockErrorView, ScheduleListPartialAlerts } from '../components/list/ScheduleListStates'
import { ScheduleListPopupHost } from '../components/list/ScheduleListPopupHost'
import { buildListSummary, countFilters, filterRows, mergeMilestones, sortRows } from '../constants/scheduleList.logic'
import { useMaterialOrderData } from '../hooks/useMaterialOrders'
import { useScheduleListData } from '../hooks/useScheduleListData'
import { useScheduleListState } from '../hooks/useScheduleListState'
import { useScheduleListActions } from '../hooks/useScheduleListActions'
import { useScheduleListRemark } from '../hooks/useScheduleListRemark'

// 리스트 보기(003 C-6) 본체 — 구현계획 D-01·D-09. 훅 4종·로직 함수·하위 컴포넌트(components/list/*)를 조립만 한다.
//  - 상태·판정은 훅(C-01~04)과 로직 파일(B-05~12)이 맡는다. 이 파일은 값을 받아 그대로 내려준다.
//  - 상단 정보줄은 GanttInfoBar 를 수정 없이 재사용한다. 화면 가이드 `?` 는 tourRef 를 넘기지 않아 숨기고,
//    인쇄 버튼은 리스트 동작이 정의되기 전까지 연결하지 않는다(계획서 6-8 기본안).
//  - 표 안 직접 입력은 없다. 수정은 간트 공종 상세창 한 곳에서 한다(openTaskDetail = navigate('gantt', scheduleId, taskId)).
//  - 현장을 바꿔도 컴포넌트를 다시 만들지 않는다(key={scheduleId} 금지) — 필터·정렬은 유지하고 펼침만 초기화(C-02).

type Props = { navigate: NavFn; selectedSite?: SiteInfo; embedded?: boolean }

type OpenDd = 'dd1' | 'dd2' | 'dd3' | null

/** 인쇄 — 리스트 동작 정의 전까지 연결하지 않음(계획서 6-8) */
const noPrint = () => {}

/** InfoBar 가 `setX(true)` 로만 부르는 열기 함수를 상태 setter 모양으로 맞춘다 */
const asOpener = (open: () => void): Dispatch<SetStateAction<boolean>> => v => { if (v === true) open() }

const EMPTY_TASKS: GanttTask[] = []
const EMPTY_DETAIL_TASKS: ScheduleDetailTask[] = []

export function ScheduleListView({ navigate, selectedSite, embedded }: Props) {
  const schedulerHost = useSchedulerHost()
  const scheduleId = selectedSite?.scheduleId ?? null

  const data = useScheduleListData(selectedSite)
  // 펼침 줄의 대표 거래처 선택에 쓰는 발주 원장 — useScheduleListData 와 같은 쿼리 키라 추가 조회는 없다
  const orders = useMaterialOrderData(scheduleId)
  const vendorRows = useMemo(() => (orders.status === 'ready' ? orders.rows : []), [orders.status, orders.rows])
  const state = useScheduleListState(scheduleId)
  const remark = useScheduleListRemark({ scheduleId, savedRemark: data.detail?.remark })
  const { openPopup, closePopup } = state

  // 리스트 → 간트 상세창(E-02: navigate 의 taskId 인자를 간트가 딥링크로 받음)
  const openTaskDetail = useCallback((taskId: number, card?: GanttDeepLink['card']) => navigate('gantt', scheduleId ?? undefined, taskId, undefined, undefined, card), [navigate, scheduleId])
  const actions = useScheduleListActions({ openPopup, siteId: selectedSite?.siteId, openTaskDetail })

  // 상단 드롭다운 열림 — 바깥 클릭으로 닫는다(캘린더·간트와 같은 방식)
  const [openDd, setOpenDd] = useState<OpenDd>(null)
  useEffect(() => {
    const close = () => setOpenDd(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  // ── 파생 값(로직 함수 호출) ───────────────────────────────────────────
  const { rows, milestones, today } = data
  const counts = useMemo(() => countFilters(rows, today), [rows, today])
  const summary = useMemo(() => buildListSummary(rows, milestones.length), [rows, milestones.length])
  const items = useMemo<ListDisplayItem[]>(
    () => mergeMilestones(sortRows(filterRows(rows, state.filter, today), state.sort), milestones, state.sort, state.filter, today),
    [rows, milestones, state.filter, state.sort, today],
  )

  // ── 상단 정보줄 값 ────────────────────────────────────────────────────
  const endDt = selectedSite?.endDate ? new Date(selectedSite.endDate) : null
  const dDay = useMemo(() => {
    if (!endDt) return undefined
    const diff = Math.round((endDt.getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000)
    return diff > 0 ? `D-${diff}` : diff === 0 ? 'D-Day' : `D+${Math.abs(diff)}`
  }, [endDt, today])

  // ── 오늘 할일 패널 행선지(캘린더 뷰 패턴) ────────────────────────────
  const { signal } = data

  // 보기 전환 — 이 화면의 키(work-type)는 유지하고 다른 보기로만 이동
  const handleViewChange = (v: string) => {
    if (v === 'gantt') navigate('gantt')
    else if (v === 'cal-b2') navigate('cal-b2')
  }

  const loading = data.status === 'loading'
  const detailTasks = (data.detail?.tasks ?? EMPTY_TASKS) as unknown as GanttTask[] // 비고 카드는 건수만 본다

  return (
    <Page layout="fixed">
      <GanttInfoBar
        selectedSite={selectedSite}
        endDt={endDt}
        dDay={dDay}
        signal={signal}
        handlePDF={noPrint}
        setOpenDd={setOpenDd}
        openDd={openDd}
        setShowSigongPanel={asOpener(() => openPopup({ kind: 'sigong' }))}
        openMaterialPopup={taskId => openPopup({ kind: 'material', focusTaskId: taskId })}
        setShowShipPanel={asOpener(() => openPopup({ kind: 'ship', focusTaskId: null }))}
        setShowStatusPanel={asOpener(() => openPopup({ kind: 'status' }))}
        setShowAddPop={asOpener(() => openPopup({ kind: 'addTask' }))}
      />

      {!selectedSite ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState title="현장을 선택해주세요" description="오른쪽 패널에서 현장 카드를 클릭하세요" />
        </div>
      ) : (
        <>
          <ScheduleListFilterBar
            filter={state.filter}
            counts={counts}
            onFilterChange={state.setFilter}
            sort={state.sort}
            onToggleSort={state.toggleSort}
            onChangeView={handleViewChange}
          />

          {data.status === 'ready' && <ScheduleListSummaryBar summary={summary} />}

          <PageScroll pad={false}>
            <div style={{ padding: 'var(--spacing-02) var(--spacing-05) var(--spacing-05)' }}>
            {data.blockError ? (
              <ScheduleListBlockErrorView error={data.blockError} />
            ) : loading ? (
              <ScheduleListLoading />
            ) : rows.length === 0 ? (
              <ScheduleListEmpty />
            ) : (
              <>
                <ScheduleListPartialAlerts
                  ordersFailed={data.ordersStatus === 'error'}
                  onRetryOrders={data.retryOrders}
                  milestonesFailed={data.milestonesStatus === 'error'}
                  onRetryMilestones={data.retryMilestones}
                />
                <ScheduleListTable
                  items={items}
                  expandedIds={state.expanded}
                  onToggleExpanded={state.toggleExpanded}
                  allRows={rows}
                  tasks={data.detail?.tasks ?? EMPTY_DETAIL_TASKS}
                  vendorRows={vendorRows}
                  today={today}
                  canRunNextAction={actions.canRunNextAction}
                  onRunNextAction={actions.runNextAction}
                  onOpenDetail={actions.openGanttDetail}
                  canOpenDetail={actions.canOpenTaskDetail}
                />
              </>
            )}
            </div>
          </PageScroll>

          <GanttRemarkBar
            loading={loading}
            tasks={detailTasks}
            remarkHidden={remark.remarkHidden}
            remarkDraft={remark.remarkDraft}
            setRemarkHidden={remark.setRemarkHidden}
            setRemarkDraft={remark.setRemarkDraft}
            scheduleRemark={remark.scheduleRemark}
            remarkSaving={remark.remarkSaving}
            handleRemarkSave={remark.handleRemarkSave}
          />
        </>
      )}

      {signal.panel && (
        <SignalTodoPanel
          anchor={signal.panel.anchor}
          data={signal.todosQ.data}
          isLoading={signal.todosQ.isLoading}
          isError={signal.todosQ.isError}
          onRetry={() => { void signal.todosQ.refetch() }}
          onClose={signal.closePanel}
          siteName={selectedSite?.siteName}
          canAct={actions.signalAction.canAct}
          onAct={item => { signal.closePanel(); actions.signalAction.act(item) }}
          onOpenTodoPage={schedulerHost?.openTodo ? () => { signal.closePanel(); schedulerHost.openTodo?.() } : undefined}
        />
      )}

      {selectedSite && (
        <ScheduleListPopupHost
          site={selectedSite}
          tasks={data.detail?.tasks ?? EMPTY_DETAIL_TASKS}
          popup={state.popup}
          onClose={closePopup}
          openPopup={openPopup}
          afterChange={actions.afterChange}
          navigate={navigate}
          embedded={embedded}
        />
      )}
    </Page>
  )
}
